import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      error: null,

      setUser: (user) => set({ user }),
      setToken: (token) => set({ token }),
      setError: (error) => set({ error }),

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
          });
          
          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.detail || 'Login failed');
          }
          
          const data = await response.json();
          set({ user: data.user, token: data.token, isLoading: false });
          return data;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      register: async (email, password, name) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, name, source: localStorage.getItem('traffic_source') || 'direct' })
          });
          
          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.detail || 'Registration failed');
          }
          
          const data = await response.json();
          set({ user: data.user, token: data.token, isLoading: false });
          return data;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      logout: () => {
        set({ user: null, token: null, error: null });
      },

      refreshUser: async () => {
        const token = get().token;
        if (!token) return;
        
        try {
          const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const user = await response.json();
            set({ user });
          } else {
            set({ user: null, token: null });
          }
        } catch (error) {
          console.error('Failed to refresh user:', error);
        }
      },

      redeemCode: async (code) => {
        const token = get().token;
        if (!token) throw new Error('Not authenticated');
        
        const response = await fetch(`${BACKEND_URL}/api/access-codes/redeem`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ code })
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Failed to redeem code');
        }
        
        const data = await response.json();
        // Refresh user to get updated access status
        await get().refreshUser();
        return data;
      },

      createAccessCode: async (maxUses = 1, expiresDays = null, customCode = null) => {
        const token = get().token;
        if (!token) throw new Error('Not authenticated');
        
        const response = await fetch(`${BACKEND_URL}/api/admin/access-codes`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ 
            code: customCode,
            max_uses: maxUses, 
            expires_days: expiresDays 
          })
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Failed to create code');
        }
        
        return await response.json();
      },

      getAccessCodes: async () => {
        const token = get().token;
        if (!token) throw new Error('Not authenticated');
        
        const response = await fetch(`${BACKEND_URL}/api/admin/access-codes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) throw new Error('Failed to get codes');
        return await response.json();
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ token: state.token, user: state.user }),
    }
  )
);

export default useAuthStore;
